import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { useStore } from 'react-redux'
import store from './store/store';
import JSZip from 'jszip';
import SceneManager from './editor/ui/SceneManager';
import Stage from './editor/ui/Stage';
import RobotModal from './RobotView/RobotModal';
import RobotControl from './RobotView/RobotControl';
import StageRouter from "./editor/ui/StageRouter";
import BlockPalette from './editor/blocks/Block';
import ScriptArea from './editor/ui/ScriptArea';
import CharacterGallery from './editor/ui/CharacterGallery';
import CategorySelector from './editor/ui/CategorySelector';
import Toolbar from './editor/ui/Toolbar';
import RightPanelControls from './editor/ui/RightPanelControls';
import BackgroundGallery from './editor/ui/BackgroundGallery';
import HeadingModal from './editor/ui/HeadingModal';
import SplashScreen from './editor/ui/SplashScreen';
import HumanDetectionFullStage from './editor/ui/HumanDetectionFullStage';
import ObjectDetectionController from './editor/ui/ObjectDetectionController';
import BarcodeScanController from './editor/ui/BarcodeScanController';
import NumberDetectionController from './editor/ui/NumberDetectionController';
import SimulatorModal from '../src/SimulatorView/SimulatorModal';
import SimulatorControls from '../src/SimulatorView/SimulatorControl';
import {
    clearScript,
    setShowHumanDetection,
    setSelectedDevice,
    setBackground,
    pushUndoState,
    addBackgroundToGallery,
    removeBackgroundFromGallery,
    addActor,
    setSimulatorOpen,
    setRobotModeOpen,  
    setAiChatOpen,
    setSceneHeading,
} from './store/sceneSlice';
import { nanoid } from '@reduxjs/toolkit';
import { undo, redo } from "./store/sceneSlice";
import HandControl from './editor/ui/HandControl';
import PaintEditorModal from "./painteditor/PaintEditorModal";
import DeviceSelectModal from "./editor/ui/DeviceSelectModal";
import ConnectionModal from "./editor/ui/ConnectionModal";
import { ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import SettingsModal from "./editor/ui/SettingsModal";
import { ThemeProvider, useTheme } from "./theme/ThemeProvider";
import { run } from "./utils/runScript";
import { findStartBlockIds } from "./utils/scriptStarts";
import { CUSTOM_ASSET_TYPES, deleteCustomAsset, loadCustomAssets, saveCustomAsset } from "./utils/customAssetStore";
import { buildScratchSb3Project } from "./utils/RealSb3";
import './App.css';

const demoActors = [
    {
        id: 'actor1',
        name: 'Stembot',
        image: './assets/characters/stembot.svg',
    },
];

// SIMULATOR BACKGROUNDS - UPDATED WITH ACTUAL IMAGE PATHS
const simulatorBackgrounds = [
    './assets/backgrounds/bg1.svg',
    './assets/backgrounds/bg2.svg',
    './assets/backgrounds/bg3.svg',
    './assets/backgrounds/bg4.svg',
    './assets/backgrounds/bg5.svg'
];

function BlockzieJrShell() {
    const dispatch = useDispatch();
    const selectedSimRobotId = useSelector((s) => s.scene?.selectedSimRobotId);
    const simulatorOpen = useSelector((s) => s.scene.simulatorOpen);
    const isRobotModeOpen = useSelector((s) => s.scene.robotModeOpen);  // 🤖 ADD THIS
    const aiChatOpen = useSelector((s) => s.scene.aiChatOpen);
    const { scenes, currentSceneIndex, backgroundGallery } = useSelector(s => s.scene);
    const currentScene = scenes[currentSceneIndex];
    const defaultSceneHeading = { text: "", color: "#222", size: 38 };
    const currentHeading = currentScene?.heading || defaultSceneHeading;
    const robotModeOpen = useSelector((s) => s.scene.robotModeOpen);
    const showHumanDetection = useSelector(s => s.scene.showHumanDetection);
    const actorIdFromScene = scenes[currentSceneIndex]?.actors?.[0]?.id;
    const store = useStore();


    // Expose Redux store globally for runScript and other utilities
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.store = store;
            window.__REDUX_STORE__ = store;
            console.log('🚧 Redux store made globally accessible as window.store and window.__REDUX_STORE__');
        }
    }, [store]);
    const [customCharacters, setCustomCharacters] = useState([]);
    const [customBackgrounds, setCustomBackgrounds] = useState([]);
    const [customSprites, setCustomSprites] = useState([]);

    const [selectedActorId, setSelectedActorId] = useState(actorIdFromScene || demoActors[0]?.id);
    const { canUndo, canRedo } = useSelector((state) => {
        const key = selectedSimRobotId
            ? `simRobot:${selectedSimRobotId}`
            : selectedActorId
                ? `actor:${selectedActorId}`
                : null;
        if (!key) {
            return { canUndo: false, canRedo: false };
        }
        const bucket = state.scene.history.byTarget[key];
        return {
            canUndo: !!bucket && (bucket.past?.length ?? 0) > 0,
            canRedo: !!bucket && (bucket.future?.length ?? 0) > 0,
        };
    });
    const [bgModalOpen, setBgModalOpen] = useState(false);
    const [headingModalOpen, setHeadingModalOpen] = useState(false);
    const [showGrid, setShowGrid] = useState(false);

    // Splash screen state and message
    const [showSplash, setShowSplash] = useState(true);
    const [splashMessage, setSplashMessage] = useState("Preparing, please wait...");

    // SIMULATOR STATE - UPDATED WITH BACKGROUND IMAGES
    const [showSimulatorModal, setShowSimulatorModal] = useState(false);
    const [currentBgIndex, setCurrentBgIndex] = useState(0);

    // Controls Block Palette Opening/Closing
    const [isPaletteOpen, setIsPaletteOpen] = useState(false);

    // Device & Connection Modal State
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [showConnectionDropdown, setShowConnectionDropdown] = useState(false);
    const [showFileDropdown, setShowFileDropdown] = useState(false);

    // Theme
    const { themeId, setThemeId } = useTheme();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [pendingThemeId, setPendingThemeId] = useState(themeId);

    useEffect(() => {
        setPendingThemeId(themeId);
    }, [themeId]);

    // Read selected device from redux
    const selectedDevice = useSelector(s => s.scene.selectedDevice);
    const validDevices = new Set(["esp32", "otto", "rpi", "arduino"]);
    const deviceValid = validDevices.has(selectedDevice);

    // PAINT EDITOR STATE
    const [openPaintEditor, setOpenPaintEditor] = useState(false);
    const [paintSource, setPaintSource] = useState(null);
    const [savePaintFn, setSavePaintFn] = useState(null);
    const [paintSaveHandler, setPaintSaveHandler] = useState(null);
    const [paintMode, setPaintMode] = useState('character'); // 'character' or 'background'
    const [paintHeading, setPaintHeading] = useState('');

    const selectedActor = scenes[currentSceneIndex]?.actors?.find(actor => actor.id === selectedActorId);
    const defaultCharacterHeading = selectedActor?.name || 'Character';
    const defaultBackgroundHeading = 'Background';

    const makeCustomName = (prefix) => {
        const timestamp = new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        return `${prefix} ${timestamp}`;
    };

    const persistCustomAsset = async (asset) => {
        const saved = await saveCustomAsset(asset);
        if (!saved) return;
        if (asset.type === CUSTOM_ASSET_TYPES.BACKGROUND) {
            setCustomBackgrounds(prev => [asset, ...prev]);
        } else if (asset.type === CUSTOM_ASSET_TYPES.SPRITE) {
            setCustomSprites(prev => [asset, ...prev]);
        }
    };

    const handleDeleteCustomBackground = async (assetId) => {
        const target = customBackgrounds.find(bg => bg.id === assetId);
        if (!target) return;

        const confirmed = window.confirm('Delete this custom background?');
        if (!confirmed) return;

        const removed = await deleteCustomAsset(assetId);
        if (!removed) return;

        setCustomBackgrounds(prev => prev.filter(bg => bg.id !== assetId));
        dispatch(removeBackgroundFromGallery(target.dataUrl));

        const currentBg = scenes[currentSceneIndex]?.background;
        if (currentBg === target.dataUrl) {
            dispatch(setBackground('#ffffff'));
        }
    };

    const handleDeleteCustomSprite = async (assetId) => {
        const target = customSprites.find(sprite => sprite.id === assetId);
        if (!target) return;

        const confirmed = window.confirm('Delete this custom sprite?');
        if (!confirmed) return;

        const removed = await deleteCustomAsset(assetId);
        if (!removed) return;

        setCustomSprites(prev => prev.filter(sprite => sprite.id !== assetId));
    };

    useEffect(() => {
        let isActive = true;
        const loadAssets = async () => {
            const { backgrounds, sprites } = await loadCustomAssets();
            if (!isActive) return;
            setCustomBackgrounds(backgrounds);
            setCustomSprites(sprites);
        };
        loadAssets();
        return () => {
            isActive = false;
        };
    }, [dispatch]);

    useEffect(() => {
        if (!Array.isArray(customBackgrounds) || !Array.isArray(backgroundGallery)) {
            return;
        }
        customBackgrounds.forEach(bg => {
            if (!backgroundGallery.includes(bg.dataUrl)) {
                dispatch(addBackgroundToGallery(bg.dataUrl));
            }
        });
    }, [customBackgrounds, backgroundGallery, dispatch]);


    // SIMULATOR FUNCTIONS
    const openSimulator = () => {
        console.log('🚀 Opening simulator...');
        setShowSimulatorModal(true);
    };

    const closeSimulator = () => {
        console.log('❌ Closing simulator...');
        setShowSimulatorModal(false);
    };

    // BACKGROUND CHANGE FUNCTION - NOW CYCLES THROUGH IMAGES
    const changeSimulatorBackground = () => {
        console.log('🎨 BEFORE change - currentBgIndex:', currentBgIndex);
        setCurrentBgIndex(prevIndex => {
            const newIndex = (prevIndex + 1) % simulatorBackgrounds.length;
            console.log('🎨 AFTER change - newIndex:', newIndex, 'Image:', simulatorBackgrounds[newIndex]);
            return newIndex;
        });
    };

    const sendUndo = () => {
        if (selectedSimRobotId) {
            dispatch(undo({ robotId: selectedSimRobotId }));
        } else if (selectedActorId) {
            dispatch(undo({ actorId: selectedActorId }));
        }
    };

    const sendRedo = () => {
        if (selectedSimRobotId) {
            dispatch(redo({ robotId: selectedSimRobotId }));
        } else if (selectedActorId) {
            dispatch(redo({ actorId: selectedActorId }));
        }
    };

    useEffect(() => {
        // Make Redux store globally accessible for runScript
        window.__REDUX_STORE__ = store;
        window.__REDUX_STORE__ = store; // best to expose both
        console.log('🚧 Redux store made globally accessible');
    }, [store]);

    useEffect(() => {
        // Show splash only for 3 seconds on initial load
        const timer = setTimeout(() => setShowSplash(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        window.openWiFiModal = () => setShowConnectionDropdown(true);
    }, []);

    useEffect(() => {
        if (!showFileDropdown) return;
        const handleClick = () => setShowFileDropdown(false);
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, [showFileDropdown]);

    useEffect(() => {
        if (actorIdFromScene) setSelectedActorId(actorIdFromScene);
    }, [actorIdFromScene, currentSceneIndex]);

    useEffect(() => {
        if (!store || typeof store.getState !== "function" || typeof store.subscribe !== "function") {
            return;
        }

        const previousPositions = new Map();
        const activeBumpPairs = new Set();

        const seedPositions = (state) => {
            const sceneState = state.scene;
            const scenes = sceneState?.scenes || [];
            scenes.forEach((scene) => {
                if (!Array.isArray(scene?.actors)) return;
                scene.actors.forEach((actor) => {
                    if (!actor?.id) return;
                    previousPositions.set(actor.id, {
                        sceneId: scene.id,
                        x: actor.x,
                        y: actor.y,
                    });
                });
            });
        };

        const handleStartOnBump = (movingActor, scene, sounds) => {
            if (!movingActor || movingActor.visible === false) return;

            const bumpStackIds = findStartBlockIds(
                movingActor.scripts,
                (block) => block?.name === "Start On Bump"
            );
            if (!bumpStackIds.length) return;

            const actorsInScene = Array.isArray(scene?.actors) ? scene.actors : [];
            const halfSizeA = 2 * (movingActor.size || 1);

            for (const other of actorsInScene) {
                if (!other || other.id === movingActor.id) continue;
                if (other.visible === false) continue;

                const halfSizeB = 2 * (other.size || 1);
                const dx = (movingActor.x || 0) - (other.x || 0);
                const dy = (movingActor.y || 0) - (other.y || 0);

                if (Math.abs(dx) < halfSizeA + halfSizeB && Math.abs(dy) < halfSizeA + halfSizeB) {
                    const pairKey = `${scene.id}:${movingActor.id}->${other.id}`;
                    if (activeBumpPairs.has(pairKey)) {
                        return;
                    }

                    activeBumpPairs.add(pairKey);
                    run(movingActor, dispatch, sounds, movingActor.id, { startBlockIds: bumpStackIds })
                        .catch((err) => console.error("Error running Start On Bump script:", err))
                        .finally(() => {
                            activeBumpPairs.delete(pairKey);
                        });
                    return;
                }
            }
        };

        seedPositions(store.getState());

        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            const sceneState = state.scene;
            if (!sceneState) return;

            const scenes = sceneState.scenes || [];
            const seenIds = new Set();

            scenes.forEach((scene) => {
                const actors = scene?.actors;
                if (!Array.isArray(actors)) return;

                actors.forEach((actor) => {
                    if (!actor?.id) return;
                    seenIds.add(actor.id);

                    const prev = previousPositions.get(actor.id);
                    const moved = !prev
                        || prev.x !== actor.x
                        || prev.y !== actor.y
                        || prev.sceneId !== scene.id;

                    if (moved) {
                        previousPositions.set(actor.id, {
                            sceneId: scene.id,
                            x: actor.x,
                            y: actor.y,
                        });
                        handleStartOnBump(actor, scene, sceneState.sounds);
                    }
                });
            });

            previousPositions.forEach((_, actorId) => {
                if (!seenIds.has(actorId)) {
                    previousPositions.delete(actorId);
                }
            });
        });

        return () => {
            unsubscribe();
        };
    }, [dispatch, store]);

    // DEBUG: Log background changes
    useEffect(() => {
        console.log('🎨 Background index changed to:', currentBgIndex, 'Image:', simulatorBackgrounds[currentBgIndex]);
    }, [currentBgIndex]);

    const handleSplashComplete = () => {
        setShowSplash(false);
    };

    function UndoRedoButton({ type, canDo, onClick }) {
        const [isPressed, setIsPressed] = useState(false);

        const iconSrc = !canDo
            ? `./assets/undo/${type}Off.svg`
            : isPressed
                ? `./assets/undo/${type}pressed.svg`
                : `./assets/undo/${type}.svg`;

        return (
            <button
                className="tl-btn"
                disabled={!canDo}
                onMouseDown={() => setIsPressed(true)}
                onMouseUp={() => setIsPressed(false)}
                onMouseLeave={() => setIsPressed(false)}
                onClick={onClick}
                style={{
                    opacity: canDo ? 1 : 0.5,
                    transform: isPressed ? "scale(0.93)" : "scale(1)",
                    transition: "transform .1s ease"
                }}
            >
                <img src={iconSrc} alt={type} draggable={false} />
            </button>
        );
    }

    // Helper function to convert blob to base64 data URL (Electron-compatible)
    const blobToBase64 = (blob, filePath = '') => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                let result = reader.result;
                if (filePath.endsWith('.svg') && !result.startsWith('data:image/svg+xml')) {
                    result = result.replace(/^data:.*?base64,/, 'data:image/svg+xml;base64,');
                }
                resolve(result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    // Enhanced save function - directly opens OS file save dialog (no popup)
    const handleSave = async () => {
        try {
            // Determine default filename based on heading or use "Blockzie-Jr"
            const headingText = currentHeading?.text?.trim();
            let defaultFilename = headingText ? headingText : 'Blockzie-Jr';
            defaultFilename = defaultFilename.replace(/[\\/:?"<>|]+/g, '').slice(0, 50) || 'Blockzie-Jr';

            const currentState = store.getState().scene;
            const zip = new JSZip();
            const { projectJson, assets, skippedBlocks } = await buildScratchSb3Project(currentState, {
                agent: 'blockzie-jr-web'
            });

            zip.file('project.json', JSON.stringify(projectJson, null, 2));
            zip.file('blockzie.json', JSON.stringify(currentState, null, 2));
            assets.forEach(asset => {
                zip.file(asset.md5ext, asset.blob);
            });

            if (skippedBlocks.length) {
                const unique = Array.from(new Set(skippedBlocks));
                console.warn('Skipped unsupported blocks in Scratch export:', unique);
            }

            const content = await zip.generateAsync({ type: 'blob' });

            if ('showSaveFilePicker' in window) {
                try {
                    const fileHandle = await window.showSaveFilePicker({
                        suggestedName: `${defaultFilename}.sb3`,
                        types: [{
                            accept: { 'application/zip': ['.sb3'] }
                        }],
                        excludeAcceptAllOption: true
                    });

                    const writable = await fileHandle.createWritable();
                    await writable.write(content);
                    await writable.close();

                    console.log('✅ Project saved successfully!');
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.error('Save picker error:', err);
                        downloadFile(content, defaultFilename);
                    }
                }
            } else {
                downloadFile(content, defaultFilename);
            }

        } catch (error) {
            console.error('❌ Save error:', error);
            alert('Error saving project. Please try again.');
        }
    };

    // Updated fallback download function
    const downloadFile = (content, filename) => {
        if (!filename.endsWith('.sb3')) {
            filename += '.sb3';
        }

        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // helper function: normalize filename
    function normalizeKey(key = "") {
        return key.split('/').pop();
    }

    // Update handleLoad to show splash with custom message
    const handleLoad = async (file) => {
        if (!file) {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.sb3,.json';
            input.onchange = (e) => {
                const selectedFile = e.target.files[0];
                if (selectedFile) {
                    handleLoad(selectedFile);
                }
            };
            input.click();
            return;
        }

        try {
            if (file.name.endsWith('.sb3')) {
                // Show splash with loading message
                setSplashMessage("Your project is being loaded...");
                setShowSplash(true);

                setTimeout(handleSplashComplete, 3000);
                const zip = new JSZip();
                const contents = await zip.loadAsync(file);

                const zipFiles = Object.keys(contents.files);
                const projectJsonFile = contents.file('project.json');

                if (!projectJsonFile) {
                    throw new Error('Invalid SB3 file: project.json not found');
                }

                const projectJsonText = await projectJsonFile.async('text');
                const projectData = JSON.parse(projectJsonText);

                const blockzieFile = contents.file('blockzie.json');
                if (blockzieFile) {
                    const blockzieText = await blockzieFile.async('text');
                    const blockzieData = JSON.parse(blockzieText);
                    dispatch({ type: 'scene/overwrite', payload: blockzieData });

                    const firstActor = blockzieData.scenes?.[blockzieData.currentSceneIndex || 0]?.actors?.[0];
                    if (firstActor) {
                        setSelectedActorId(firstActor.id);
                    }
                    return;
                }

                const markerFile = contents.file('scratchjr.marker');
                const isScratchJrFormat = (
                    markerFile ||
                    projectData.projectMetadata?.fileType === 'scratchjr-sb3' ||
                    projectData.fileType === 'scratchjr-sb3' ||
                    (projectData.scenes && Array.isArray(projectData.scenes))
                );

                if (isScratchJrFormat) {
                    console.log('🎯 Loading Blockzie-Jr project with Electron-compatible images...');

                    const assetDataMap = new Map();
                    let assetsLoaded = 0;

                    for (const filePath of zipFiles) {
                        if (filePath.match(/\.(svg|png|jpg|jpeg|gif|bmp|webp)$/i) && !filePath.startsWith('sounds/')) {
                            const assetFile = contents.file(filePath);
                            if (assetFile) {
                                try {
                                    const blob = await assetFile.async('blob');
                                    const base64DataUrl = await blobToBase64(blob, filePath);
                                    const key = normalizeKey(filePath);
                                    assetDataMap.set(key, base64DataUrl);
                                    assetsLoaded++;
                                    console.log(`✅ Image converted to base64: ${filePath}`);
                                } catch (error) {
                                    console.warn('Image conversion failed:', filePath, error);
                                }
                            }
                        }
                    }

                    const customSounds = [];
                    const soundFiles = zipFiles.filter(name => name.startsWith('sounds/'));
                    for (const soundPath of soundFiles) {
                        const soundFile = contents.file(soundPath);
                        if (soundFile) {
                            try {
                                const blob = await soundFile.async('blob');
                                const base64DataUrl = await blobToBase64(blob);
                                const soundId = soundPath.replace('sounds/', '').replace('.wav', '');
                                customSounds.push({
                                    id: soundId,
                                    name: `Custom Sound ${soundId.slice(-4)}`,
                                    audioURL: base64DataUrl,
                                    audioBlob: blob,
                                    type: 'custom'
                                });
                            } catch (error) {
                                console.warn('Sound conversion failed:', soundPath);
                            }
                        }
                    }

                    const restoredProject = {
                        scenes: projectData.scenes?.map(scene => ({
                            ...scene,
                            actors: scene.actors?.map(actor => {
                                const key = normalizeKey(actor.image);
                                const resolvedImage = assetDataMap.get(key) || actor.image;
                                return {
                                    ...actor,
                                    image: resolvedImage,
                                    scripts: actor.scripts || []
                                };
                            })
                        })) || [],
                        currentSceneIndex: projectData.currentSceneIndex || 0,
                        sceneUndoStack: projectData.sceneUndoStack || [],
                        sceneRedoStack: projectData.sceneRedoStack || [],
                        selectedBlockCategory: projectData.selectedBlockCategory || 'motion',
                        categoryPanelOpen: projectData.categoryPanelOpen || false,
                        sounds: projectData.sounds ? Object.fromEntries(
                            Object.entries(projectData.sounds).map(([key, value]) => [
                                key, assetDataMap.get(normalizeKey(value)) || value
                            ])
                        ) : { pop: './assets/sounds/pop.mp3' },
                        backgroundGallery: projectData.backgroundGallery?.map(bg =>
                            assetDataMap.get(normalizeKey(bg)) || bg
                        ) || [],
                        customSounds: [...(projectData.customSounds || []), ...customSounds]
                    };

                    console.log('📊 Assets loaded:', assetsLoaded);

                    dispatch({ type: 'scene/overwrite', payload: restoredProject });

                    setTimeout(() => {
                        const firstActor = restoredProject.scenes[restoredProject.currentSceneIndex]?.actors?.[0];
                        if (firstActor) {
                            setSelectedActorId(null);
                            setTimeout(() => {
                                setSelectedActorId(firstActor.id);
                                console.log('🎭 Actor selected with base64 image');
                            }, 100);
                        }
                    }, 200);

                }

            } else if (file.name.endsWith('.json')) {
                const text = await file.text();
                const data = JSON.parse(text);
                dispatch({ type: 'scene/overwrite', payload: data });

                const firstActor = data.scenes?.[data.currentSceneIndex || 0]?.actors?.[0];
                if (firstActor) {
                    setSelectedActorId(firstActor.id);
                }
            }

        } catch (error) {
            console.error('❌ Load error:', error);
            alert('Error loading project. Please check the file format.');
        }
    };

    const openBackgroundPainter = useCallback(() => {
        console.log('🎨 Opening background paint editor');
        setPaintMode('background');
        setPaintHeading(defaultBackgroundHeading);
        setBgModalOpen(false);
        setOpenPaintEditor(true);
    }, [defaultBackgroundHeading]);

    const openCharacterPainter = useCallback(() => {
        console.log('🎨 Opening character paint editor');
        setPaintMode('character');
        setPaintHeading(defaultCharacterHeading);
        setOpenPaintEditor(true);
    }, [defaultCharacterHeading]);

    const handleOpenBackgroundPaint = () => {
        openBackgroundPainter();
    };

    // ADD THIS NEW CODE:
    useEffect(() => {
        window.openBackgroundPaint = () => {
            console.log('🟢 GLOBAL FUNCTION CALLED!');
            openBackgroundPainter();
        };

        return () => {
            delete window.openBackgroundPaint;
        };
    }, [openBackgroundPainter]);

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => alert('Fullscreen request denied'));
        } else {
            document.exitFullscreen();
        }
    };

    const handleGridToggle = () => {
        setShowGrid(g => !g);
    };

    const handleApplyTheme = () => {
        setThemeId(pendingThemeId);
        setSettingsOpen(false);
    };

    const handleGreenFlag = () => {
        dispatch(setShowHumanDetection(true)); // PATCH: always enable detection
        if (!selectedActorId) return;
        const currentScene = scenes[currentSceneIndex];
        if (!currentScene) return;

        const actor = currentScene.actors.find(a => a.id === selectedActorId);
        if (!actor || !actor.scripts?.length) return;

        actor.scripts.forEach((script) => {
            if (script.execute && typeof script.execute === 'function') {
                script.execute();
            } else if (script.soundData && script.soundData.audioURL) {
                const audio = new Audio(script.soundData.audioURL);
                audio.play().catch(err => console.error('Sound execution error:', err));
            }

            if (script.blocks && script.blocks.length > 0 && script.blocks[0].type === "green_flag") {
                dispatch({
                    type: 'script/run',
                    payload: { actorId: actor.id, scriptId: script.id }
                });
            }
        });
    };

    const handleBrush = (actor) => {
        // Paint editor functionality
    };

    const handleAddCharacter = () => {
        // Add character functionality
    };

    if (showSplash) {
        return <SplashScreen onComplete={handleSplashComplete} message={splashMessage} />;
    }

    return (
        <div className="app-container">
            {/* Top Navbar with simplified Toolbar */}
            <header className="top-navbar">
                {/* <Toolbar
 selectedActorId={selectedActorId}
 onSave={handleSave}
 onLoad={handleLoad}
 heading={currentHeading}
 onOpenHumanDetection={() => dispatch(setShowHumanDetection(true))}
 /> */}
            </header>

            {/* --- KEY: Both overlays are rendered together and only when Human Detection is ON --- */}
            <HumanDetectionFullStage
                isOpen={showHumanDetection}
                onClose={() => dispatch(setShowHumanDetection(false))}
                selectedActorId={selectedActorId}
            />
            {showHumanDetection && <HandControl cameraActive={true} />}
            <ObjectDetectionController selectedActorId={selectedActorId} />
            <BarcodeScanController selectedActorId={selectedActorId} />
            <NumberDetectionController selectedActorId={selectedActorId} />

            <BackgroundGallery
                open={bgModalOpen}
                customBackgrounds={customBackgrounds}
                onDeleteCustomBackground={handleDeleteCustomBackground}
                onClose={() => {
                    console.log('🔴 BG Modal closing');
                    setBgModalOpen(false);
                }}
                onPaint={() => {
                    console.log('🟢 APP.JS onPaint called!');
                    console.log('🟢 paintMode before:', paintMode);
                    console.log('🟢 openPaintEditor before:', openPaintEditor);
                    openBackgroundPainter();
                    console.log('🟢 setOpenPaintEditor(true) called!');
                }}
            />



            <HeadingModal
                open={headingModalOpen}
                onClose={() => setHeadingModalOpen(false)}
                onSave={h => {
                    dispatch(setSceneHeading({ sceneIndex: currentSceneIndex, heading: h }));
                    setHeadingModalOpen(false);
                }}
                initial={currentHeading}
            />
            <PaintEditorModal
                open={openPaintEditor}
                initialHeading={paintHeading}
                onHeadingChange={setPaintHeading}
                onClose={() => {
                    setOpenPaintEditor(false);
                    setPaintMode('character');
                    setPaintSaveHandler(null);
                    setPaintHeading('');
                }}
                onSave={(imageDataURL, headingValue) => {
                    console.log('💾 Saving...', paintMode);

                    const resolvedHeading = (headingValue ?? paintHeading ?? '').trim();

                    if (paintMode === 'background') {
                        // BACKGROUND MODE
                        console.log('🖼️ Setting custom background');
                        dispatch(pushUndoState());
                        dispatch(setBackground(imageDataURL));
                        dispatch(addBackgroundToGallery(imageDataURL));
                        const backgroundAsset = {
                            id: nanoid(),
                            type: CUSTOM_ASSET_TYPES.BACKGROUND,
                            name: resolvedHeading || makeCustomName('Custom Background'),
                            dataUrl: imageDataURL,
                            createdAt: Date.now()
                        };
                        persistCustomAsset(backgroundAsset);
                        setOpenPaintEditor(false);
                        setPaintMode('character');
                        setPaintHeading('');
                        console.log('✅ Background saved!');
                    } else {
                        // CHARACTER MODE
                        if (typeof paintSaveHandler === 'function') {
                            paintSaveHandler(imageDataURL);
                        }
                        const generatedName = `Custom-${new Date().toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                        })}`;

                        const customActor = {
                            id: nanoid(),
                            name: resolvedHeading || generatedName,
                            image: imageDataURL,
                            isCustom: true,
                            x: 19,
                            y: 11,
                            direction: 0,
                            size: 1,
                            visible: true,
                            scripts: []
                        };

                        dispatch(addActor(customActor));
                        const spriteAsset = {
                            id: nanoid(),
                            type: CUSTOM_ASSET_TYPES.SPRITE,
                            name: customActor.name,
                            dataUrl: imageDataURL,
                            createdAt: Date.now()
                        };
                        persistCustomAsset(spriteAsset);
                        setSelectedActorId(customActor.id);
                        setOpenPaintEditor(false);
                        setPaintSaveHandler(null);
                        setPaintHeading('');
                        console.log('✅ Character saved!');
                    }
                }}
            />

            {/* Main Content - 2 Column Layout: Script | Right Column */}
            <main className="middle-section">
                {/* LEFT: big Script area */}
                <section className="script-area-section">
                    <ScriptArea selectedActorId={selectedActorId} />
                </section>

                {/* RIGHT: controls + stage + scene & characters */}
                <section className="right-panel-section">
                    {/* TOP: Right panel controls row - ALWAYS VISIBLE */}
                    <div className="right-controls-wrapper">
                        <RightPanelControls
                            onFullScreen={handleFullscreen}
                            onGridToggle={handleGridToggle}
                            onHeading={() => setHeadingModalOpen(true)}
                            onGreenFlag={handleGreenFlag}
                            selectedActorId={selectedActorId}
                            onSimulator={openSimulator}
                            onBackgroundChange={changeSimulatorBackground}
                        />
                    </div>

                    {/* CONDITIONAL: Robot Mode or Normal Mode */}
                    {robotModeOpen ? (
                        /* 🤖 ROBOT MODE - Replaces Stage, Scenes, Characters */
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}>
                            <RobotControl />
                        </div>
                    ) : (
                        /* 👾 NORMAL MODE - Original Stage, Scenes, Characters */
                        <>
                            {/* MIDDLE: Stage card */}
                            <div className="stage-wrapper">
                                <StageRouter
                                    selectedActorId={selectedActorId}
                                    setSelectedActorId={setSelectedActorId}
                                    heading={currentHeading}
                                    showGrid={showGrid}
                                />
                            </div>

                            {/* BOTTOM: Scene & Characters cards side-by-side */}
                            <div className="scene-character-wrapper">
                                <div className="scene-manager-container">
                                    <SceneManager />
                                </div>
                                <div className="character-gallery-container">
                                    <CharacterGallery
                                        selectedActorId={selectedActorId}
                                        setSelectedActorId={setSelectedActorId}
                                        onBrush={handleBrush}
                                        onAdd={handleAddCharacter}
                                        onPaintRequest={openCharacterPainter}
                                        customCharacters={customCharacters}
                                        customSprites={customSprites}
                                        onDeleteCustomSprite={handleDeleteCustomSprite}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </section>

            </main>

            <footer className="blocks-footer">
                {/* LEFT SIDE SETTINGS ICON */}
                <div className='left-block'>
                    <div className="footer-left">
                        <button className="footer-icon-btn" onClick={() => setSettingsOpen(true)}>
                            ⚙️
                        </button>
                    </div>

                    {/* CENTER CATEGORY BAR */}
                    <div className="category-center">
                        <CategorySelector
                            isPaletteOpen={isPaletteOpen}
                            setIsPaletteOpen={setIsPaletteOpen}
                        />
                    </div>

                    <BlockPalette isPaletteOpen={isPaletteOpen} />
                </div>

                {/* RIGHT SIDE ICONS */}
                <div className="footer-right">
                    <div className="footer-file-dropdown">
                        <button
                            className="footer-icon-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowFileDropdown((v) => !v);
                            }}
                            title="File"
                        >
                            📁
                        </button>

                        {showFileDropdown && (
                            <div
                                className="file-dropdown-panel"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="file-dropdown-title">File</div>

                                <button
                                    className="file-dropdown-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowFileDropdown(false);
                                        handleSave();
                                    }}
                                >
                                    <span className="file-dropdown-emoji" aria-hidden="true">💾</span>
                                    <span>Save Project</span>
                                </button>

                                <button
                                    className="file-dropdown-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowFileDropdown(false);
                                        handleLoad();
                                    }}
                                >
                                    <span className="file-dropdown-emoji" aria-hidden="true">📂</span>
                                    <span>Open Project</span>
                                </button>

                                <div className="file-dropdown-divider" />

                                <button
                                    className="file-dropdown-close"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowFileDropdown(false);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        className="footer-icon-btn"
                        onClick={() => dispatch(setAiChatOpen(!aiChatOpen))}
                        title="AI Chat"
                        aria-pressed={aiChatOpen}
                    >
                        <img src="./assets/ui/StembotAI.png" alt="AI Chat" draggable={false} />
                    </button>

                    <button
                        className="footer-icon-btn"
                        onClick={() => dispatch(setSimulatorOpen(!simulatorOpen))}
                        title="Simulator"
                    >
                        <img src="./assets/ui/simulator.png" alt="Simulator" draggable={false} />
                    </button>

                    {/* 🤖 ROBOT MODE BUTTON */}
                    <button
                        className="footer-icon-btn"
                        onClick={() => dispatch(setRobotModeOpen(!isRobotModeOpen))}
                        title={isRobotModeOpen ? "Close Robot Mode" : "Open Robot Mode"}
                    >
                        <span style={{ fontSize: '28px' }}>
                            🤖
                        </span>
                    </button>

                    <button className="footer-icon-btn" onClick={() => setShowDeviceModal(true)}>
                        🔌
                    </button>

                    <button
                        className="footer-icon-btn connection-toggle-btn"
                        onClick={() => deviceValid && setShowConnectionDropdown(true)}
                        disabled={!deviceValid}
                        style={{
                            opacity: deviceValid ? 1 : 0.4,
                            cursor: deviceValid ? "pointer" : "not-allowed"
                        }}
                    >
                        🔗
                    </button>

                    <UndoRedoButton type="undo" canDo={canUndo} onClick={sendUndo} />
                    <UndoRedoButton type="redo" canDo={canRedo} onClick={sendRedo} />

                </div>




                {/* Device Select Modal */}
                {showDeviceModal && (
                    <DeviceSelectModal
                        onClose={() => setShowDeviceModal(false)}
                        onSelectDevice={(key) => {
                            dispatch(setSelectedDevice(key));
                            setShowDeviceModal(false);
                            setShowConnectionDropdown(false);
                        }}
                    />
                )}

                {/* Connection Modal */}
                <ConnectionModal
                    isOpen={showConnectionDropdown}
                    onClose={() => setShowConnectionDropdown(false)}
                />

                <SettingsModal
                    open={settingsOpen}
                    selectedThemeId={pendingThemeId}
                    onSelect={setPendingThemeId}
                    onApply={handleApplyTheme}
                    onClose={() => setSettingsOpen(false)}
                />

            </footer>
            <HandControl />
            <ToastContainer position="bottom-right" autoClose={2000} />
        </div>
    );
}

export default function App() {
    return (
        <Provider store={store}>
            <ThemeProvider>
                <BlockzieJrShell />
            </ThemeProvider>
        </Provider>
    );
}





